// A deck's look made the user's own (S6, 25 Oct 2026, David: "a slimmed-down version of PowerPoint where you can create
// your own format, your own themes, any background images"). Three things, all plain data:
//
// - A theme: the deck's colours (shared/slideDesign.ts THEME_COLORS, so every box using one follows it, as PowerPoint's
//   theme colours do), its title and body fonts (from the board's own list), the background every slide starts with,
//   whether title and section slides stand on the accent, and what goes on every slide (a logo, footer words, the
//   slide number). The deck keeps its own copy of its theme's values (DeckData.theme), so a shared link, a coworker and
//   a later change to a saved theme never change a deck by themselves.
// - Saved themes: the workspace's own (a table of its own on the server, worker/deckThemes.ts), which any deck in it
//   can wear. A deck that wears one remembers which and at what version, to offer "Update this deck" when it moves on.
// - Layouts of the user's own ("your own format"): a slide's arrangement kept under a name, the deck's (DeckData.layouts),
//   travelling with its theme when the theme is saved. Applying one moves a slide's words into its places.
//
// Colours are hex or theme colours by name, fonts come only from the list, pictures only as the board's own kept keys.
// No CSS, addresses or HTML anywhere in here. Light on purpose: the board, the server and the agents' tools read it.

import { boxWords, cleanBg, cleanBoxes, cleanColor, cleanFont, cleanPlace, boxLimits, FONT, THEME_COLORS, type Palette, type SlideBg, type SlideBox, type SlidePlace, type ThemeColorId } from "./slideDesign";
import { cleanPictureRef, type PictureRef } from "./imageInfo";
import type { DocNode } from "./docblocks";
import { cleanLayoutMaster, layoutBase, type LayoutMaster } from "./slideMaster";

// ---------------------------------------------------------------------------
// limits

/** The most themes a workspace keeps, as it keeps 40 board themes. */
export const MAX_SAVED_THEMES = 40;
/** A theme as JSON, its layouts included: a few dozen layouts of boxes fit, a pasted book does not. */
export const MAX_THEME_BYTES = 160_000;
/** The most layouts of its own a deck (and a saved theme) holds. */
export const MAX_LAYOUTS = 24;
export const MAX_THEME_NAME = 60;
export const MAX_LAYOUT_NAME = 40;
export const MAX_FOOTER = 120;
const MAX_PROMPT = 200;

/** Why a theme cannot be kept, in words a person or an agent reads. */
export class ThemeRefused extends Error {}

const HEX = /^#[0-9A-F]{6}$/;
const LAYOUT_ID = /^L[A-Za-z0-9]{4,16}$/;
const SAVED_ID = /^dt_[A-Za-z0-9_-]{4,40}$/;

/** A name as kept: one line of plain words, without anything that could be read as markup. */
export function cleanName(raw: unknown, max: number): string {
  if (typeof raw !== "string") return "";
  return raw
    .replace(/[\u0000-\u001f\u007f<>]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max)
    .trim();
}

const hexOf = (raw: unknown): string | null => {
  const c = cleanColor(raw);
  return c && HEX.test(c) ? c : null;
};

// ---------------------------------------------------------------------------
// what goes on every slide

export type Corner = "tl" | "tr" | "bl" | "br";
export const CORNERS: Corner[] = ["tl", "tr", "bl", "br"];
/** Where the slide number sits: a bottom corner, the bottom's middle, or the top right. */
export type NumberPlace = "br" | "bl" | "bc" | "tr";
export const NUMBER_PLACES: NumberPlace[] = ["br", "bl", "bc", "tr"];
/** Which slides the every-slide items leave out: title slides (as PowerPoint's "Don't show on title slide"), title and section slides, or none. */
export type ChromeSkip = "title" | "key" | "none";

export interface DeckChrome {
  /** a picture in a corner of every slide, its height in the slide's pixels */
  logo?: { image: PictureRef; corner: Corner; size: number };
  footer?: string;
  /** the date beside the footer (Fixes 8): today's, which moves on by itself (PowerPoint's Update automatically), or fixed words */
  date?: DeckDate;
  /** the slide number, where it sits; absent: none */
  number?: NumberPlace;
  /** the slides they leave out (absent: title slides) */
  skip?: ChromeSkip;
}

export type DeckDate = { auto: true } | { text: string };

/** The date as a slide shows it: today's in the viewer's own way of writing dates, or the fixed words. */
export function dateWords(date: DeckDate | undefined, now = Date.now()): string {
  if (!date) return "";
  if ("text" in date) return date.text;
  return new Date(now).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
}

export const LOGO_MIN = 24;
export const LOGO_MAX = 200;

export function cleanChrome(raw: unknown): DeckChrome | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const out: DeckChrome = {};
  const lg = r.logo as Record<string, unknown> | undefined;
  const image = lg && typeof lg === "object" ? cleanPictureRef(lg.image) : null;
  if (image) {
    const corner = CORNERS.includes(lg!.corner as Corner) ? (lg!.corner as Corner) : "tr";
    const size = typeof lg!.size === "number" && Number.isFinite(lg!.size) ? Math.round(Math.max(LOGO_MIN, Math.min(LOGO_MAX, lg!.size))) : 56;
    out.logo = { image, corner, size };
  }
  const footer = typeof r.footer === "string" ? cleanName(r.footer, MAX_FOOTER) : "";
  if (footer) out.footer = footer;
  const d = r.date as Record<string, unknown> | undefined;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const words = typeof d.text === "string" ? cleanName(d.text, MAX_FOOTER) : "";
    if (d.auto === true) out.date = { auto: true };
    else if (words) out.date = { text: words };
  }
  if (NUMBER_PLACES.includes(r.number as NumberPlace)) out.number = r.number as NumberPlace;
  if (r.skip === "key" || r.skip === "none") out.skip = r.skip;
  return Object.keys(out).length ? out : null;
}

export type ChromeItem = "logo" | "date" | "footer" | "number";

/**
 * Where each every-slide item goes: the number where the theme says, the date and the footer at the bottom left (or
 * right, when the number is there), the date nearest the corner, the logo in its corner.
 */
export function chromeSpots(chrome: DeckChrome): Map<Corner | "bc", ChromeItem[]> {
  const spots = new Map<Corner | "bc", ChromeItem[]>();
  const add = (s: Corner | "bc", what: ChromeItem) => spots.set(s, [...(spots.get(s) ?? []), what]);
  if (chrome.number) add(chrome.number, "number");
  if (chrome.date) add(chrome.number === "bl" ? "br" : "bl", "date");
  if (chrome.footer) add(chrome.number === "bl" ? "br" : "bl", "footer");
  if (chrome.logo) add(chrome.logo.corner, "logo");
  return spots;
}

/** Whether a slide of this layout leaves the every-slide items out. */
export function chromeSkips(chrome: DeckChrome | null | undefined, layout: string): boolean {
  const skip = chrome?.skip ?? "title";
  if (skip === "none") return false;
  if (layout === "title") return true;
  return skip === "key" && layout === "section";
}

// ---------------------------------------------------------------------------
// a theme

export interface DeckFonts {
  /** a font of the board's list (shared/slideDesign.ts FONTS), by id */
  title: string;
  body: string;
}

export interface DeckTheme {
  name: string;
  /** every theme colour as #RRGGBB */
  colors: Palette;
  fonts: DeckFonts;
  /** the background every slide starts with; absent: the background colour */
  bg?: SlideBg | null;
  /** title and section slides stand on the accent, as the Bold style's do */
  key?: "accent";
  chrome?: DeckChrome | null;
  /** the saved theme it was taken from, at the version taken, to offer "Update this deck" when it has moved on */
  from?: { id: string; v: number } | null;
}

/** A theme's colours as kept: each one given as a hex, else the one it had, else a sensible one worked out from the rest. */
export function cleanColors(raw: unknown, before?: Palette | null): Palette | null {
  const r = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const pick = (id: ThemeColorId, ...words: string[]) => hexOf(r[id]) ?? words.map((w) => hexOf(r[w])).find(Boolean) ?? before?.[id] ?? null;
  const background = pick("background", "bg", "ground");
  const text = pick("text", "ink", "fg");
  if (!background || !text) return null;
  const accent = pick("accent", "primary", "accent1") ?? text;
  return {
    background,
    surface: pick("surface", "card") ?? mixHex(background, text, 0.06),
    text,
    muted: pick("muted", "muted-text", "secondary") ?? mixHex(text, background, 0.32),
    accent,
    accent2: pick("accent2") ?? accent,
    accent3: pick("accent3") ?? accent,
    accent4: pick("accent4") ?? accent,
  };
}

/** A font as a theme keeps it: one of the list by its id, or null. */
const fontId = (raw: unknown): string | null => {
  const f = cleanFont(raw);
  return f && f !== "title" && f !== "body" && FONT[f] ? f : null;
};

/**
 * A theme as a deck or the workspace keeps it, from what was sent. A field sent replaces the one it had, one not sent
 * keeps it (`before`), and a colour, font or picture the board does not take is left out, never kept as written.
 */
export function cleanDeckTheme(raw: unknown, before?: DeckTheme | null): DeckTheme | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const has = (k: string) => Object.hasOwn(r, k);
  const colors = cleanColors(r.colors, before?.colors);
  if (!colors) return null;
  const f = r.fonts && typeof r.fonts === "object" ? (r.fonts as Record<string, unknown>) : {};
  const fonts = { title: fontId(f.title) ?? before?.fonts.title ?? "geist", body: fontId(f.body) ?? before?.fonts.body ?? "geist" };
  const name = cleanName(r.name, MAX_THEME_NAME) || before?.name || "Your theme";
  const bg = has("bg") ? cleanBg(r.bg) : (before?.bg ?? null);
  const key = has("key") ? (r.key === "accent" ? "accent" : undefined) : before?.key;
  const chrome = has("chrome") ? cleanChrome(r.chrome) : (before?.chrome ?? null);
  const fr = has("from") ? r.from : before?.from;
  const from = fr && typeof fr === "object" && typeof (fr as { id?: unknown }).id === "string" && SAVED_ID.test((fr as { id: string }).id) ? { id: (fr as { id: string }).id, v: Math.max(1, Math.round(Number((fr as { v?: unknown }).v) || 1)) } : null;
  return { name, colors, fonts, ...(bg ? { bg } : {}), ...(key ? { key } : {}), ...(chrome ? { chrome } : {}), ...(from ? { from } : {}) };
}

// ---------------------------------------------------------------------------
// layouts of the user's own

/** A layout's fields that take words, whose words a saved layout keeps as the prompts a new slide shows. */
export const PROMPT_FIELDS = ["title", "body", "body2", "figure", "cite"] as const;
export type PromptField = (typeof PROMPT_FIELDS)[number];

/** An empty place for a picture, where a saved layout's picture was (as PowerPoint's picture placeholder). */
export interface PictureSlot {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  radius?: number;
}

/** How a layout looks drawn small in the layout strip: its words, pictures and shapes as blocks, measured as it was saved. */
export interface GlyphPart {
  k: "t" | "w" | "p" | "s" | "l";
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface SavedLayout {
  id: string;
  name: string;
  /** the board's layout it builds on: its fields are the slide's title and text, wherever they sit */
  base: string;
  /** where its fields sit and how they look */
  place?: SlidePlace;
  /** its boxes; a text box's or a shape's words are the prompt a new slide shows in it, and it starts empty */
  boxes?: SlideBox[];
  /** the prompts its fields show, from the words the slide had */
  prompts?: Partial<Record<PromptField, string>>;
  /** empty places for pictures */
  slots?: PictureSlot[];
  /** a picture kept in its picture field, for slides that have none */
  image?: PictureRef;
  bg?: SlideBg | null;
  glyph?: GlyphPart[];
  /** SC: its pieces as slides made from it were given them, kept when it is first edited in the slide master, so those
   * slides follow it (shared/slideMaster.ts) */
  was?: LayoutMaster;
}

const LAYOUT_BASES = new Set(["title", "section", "text", "two", "big", "quote", "picture", "full", "widget", "blank"]);
const n1 = (v: unknown, lo: number, hi: number): number | null => (typeof v === "number" && Number.isFinite(v) ? Math.round(Math.max(lo, Math.min(hi, v)) * 10) / 10 : null);

function cleanSlot(raw: unknown): PictureSlot | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || !/^[A-Za-z0-9_-]{2,24}$/.test(r.id)) return null;
  const x = n1(r.x, -800, 2400);
  const y = n1(r.y, -450, 1350);
  const w = n1(r.w, 16, 3200);
  const h = n1(r.h, 16, 1800);
  if (x === null || y === null || w === null || h === null) return null;
  const radius = n1(r.radius, 0, 900);
  return { id: r.id, x, y, w, h, ...(radius ? { radius } : {}) };
}

function cleanGlyph(raw: unknown): GlyphPart[] {
  if (!Array.isArray(raw)) return [];
  const out: GlyphPart[] = [];
  for (const p of raw.slice(0, 32)) {
    if (!p || typeof p !== "object") continue;
    const r = p as Record<string, unknown>;
    const k = r.k === "t" || r.k === "w" || r.k === "p" || r.k === "s" || r.k === "l" ? r.k : null;
    const x = n1(r.x, -1600, 3200);
    const y = n1(r.y, -900, 1800);
    const w = n1(r.w, 0, 3200);
    const h = n1(r.h, 0, 1800);
    if (k && x !== null && y !== null && w !== null && h !== null) out.push({ k, x, y, w, h });
  }
  return out;
}

export function cleanSavedLayout(raw: unknown): SavedLayout | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || !LAYOUT_ID.test(r.id)) return null;
  const name = cleanName(r.name, MAX_LAYOUT_NAME);
  if (!name) return null;
  const base = typeof r.base === "string" && LAYOUT_BASES.has(r.base) ? r.base : "blank";
  const place = cleanPlace(r.place);
  // a live widget is the board's data, not a layout's: a saved layout never carries one
  const boxes = cleanBoxes(r.boxes).filter((b) => b.kind !== "widget");
  if (boxLimits(boxes)) return null;
  const pr = r.prompts && typeof r.prompts === "object" ? (r.prompts as Record<string, unknown>) : {};
  const prompts: Partial<Record<PromptField, string>> = {};
  for (const f of PROMPT_FIELDS) {
    const p = cleanName(pr[f], MAX_PROMPT);
    if (p) prompts[f] = p;
  }
  const slots = (Array.isArray(r.slots) ? r.slots.slice(0, 24) : []).map(cleanSlot).filter((s): s is PictureSlot => !!s);
  const image = cleanPictureRef(r.image);
  const bg = cleanBg(r.bg);
  const glyph = cleanGlyph(r.glyph);
  const was = cleanLayoutMaster(r.was);
  return {
    id: r.id,
    name,
    base,
    ...(Object.keys(place).length ? { place } : {}),
    ...(boxes.length ? { boxes } : {}),
    ...(Object.keys(prompts).length ? { prompts } : {}),
    ...(slots.length ? { slots } : {}),
    ...(image ? { image } : {}),
    ...(bg ? { bg } : {}),
    ...(glyph.length ? { glyph } : {}),
    ...(was ? { was } : {}),
  };
}

/** A deck's (or a saved theme's) layouts as kept: each cleaned, one id once, a name once (the later kept), up to MAX_LAYOUTS. */
export function cleanLayouts(raw: unknown): SavedLayout[] {
  if (!Array.isArray(raw)) return [];
  const out: SavedLayout[] = [];
  for (const l of raw) {
    const c = cleanSavedLayout(l);
    if (!c || out.some((o) => o.id === c.id)) continue;
    out.push(c);
  }
  if (out.length > MAX_LAYOUTS) throw new ThemeRefused(`A deck keeps up to ${MAX_LAYOUTS} layouts of its own; this would be ${out.length}. Delete one first.`);
  return out;
}

/** A deck's layouts with others laid over them by id (a theme's arriving on a deck): the newer one of an id wins. */
export function mergeLayouts(mine: SavedLayout[], theirs: SavedLayout[]): SavedLayout[] {
  const out = [...mine];
  for (const l of theirs) {
    const at = out.findIndex((o) => o.id === l.id);
    if (at >= 0) out[at] = l;
    else out.push(l);
  }
  return out.slice(0, MAX_LAYOUTS);
}

/** A saved layout named as a person or an agent writes it: by its id or its name, any case. */
export function findLayout(layouts: SavedLayout[] | undefined, ref: unknown): SavedLayout | null {
  if (typeof ref !== "string" || !ref.trim()) return null;
  const k = ref.trim().toLowerCase();
  return (layouts ?? []).find((l) => l.id.toLowerCase() === k || l.name.toLowerCase() === k) ?? null;
}

/** A new layout's id. */
export const layoutId = (rand: () => string) => "L" + rand().replace(/[^A-Za-z0-9]/g, "").slice(0, 9).padEnd(5, "0");

// ---------------------------------------------------------------------------
// saving a slide as a layout, and putting a layout on a slide

/** The slide fields a layout reads and writes (shared/slides.ts SlideData, by what this needs of it). */
export interface LayoutSlide {
  layout: string;
  title: string;
  body: { content: DocNode[] } | null;
  body2?: { content: DocNode[] } | null;
  figure?: string;
  cite?: string;
  image?: PictureRef | null;
  boxes?: SlideBox[];
  place?: SlidePlace;
  bg?: SlideBg | null;
  lay?: string;
}

const plain = (nodes: DocNode[] | undefined): string => {
  const walk = (n: DocNode): string => (n.type === "text" ? (n.text ?? "") : n.type === "hardBreak" ? " " : (n.content ?? []).map(walk).join(" "));
  return (nodes ?? []).map(walk).join(" ").replace(/\s+/g, " ").trim();
};

/**
 * A slide's arrangement as a layout of its own: where its fields and boxes sit and how they look, and its background.
 * Its words become the prompts a new slide shows. Each picture becomes an empty place for a picture (the PowerPoint
 * way) unless `keep` names it (a box's id, or "image" for the layout's picture): then the layout carries it. A widget
 * stays the slide's.
 */
export function layoutFromSlide(s: LayoutSlide, opts: { id: string; name: string; keep?: string[]; glyph?: GlyphPart[] }): SavedLayout {
  const keep = new Set(opts.keep ?? []);
  const prompts: Partial<Record<PromptField, string>> = {};
  // a text's prompt is its first line, as a placeholder's is one line ("Click to add text")
  const first = (nodes: DocNode[] | undefined) => {
    const b = nodes?.[0];
    const item = b && (b.type === "bulletList" || b.type === "orderedList") ? b.content?.[0]?.content?.[0] : b;
    return item ? plain([item]) : "";
  };
  const words: Record<PromptField, string> = { title: s.title.replace(/\s+/g, " ").trim(), body: first(s.body?.content), body2: first(s.body2?.content), figure: s.figure ?? "", cite: s.cite ?? "" };
  for (const f of PROMPT_FIELDS) if (words[f]) prompts[f] = words[f].slice(0, MAX_PROMPT);
  const slots: PictureSlot[] = [];
  const boxes: SlideBox[] = [];
  for (const b of s.boxes ?? []) {
    if (b.kind === "widget") continue;
    if (b.kind === "picture" && !keep.has(b.id)) {
      slots.push({ id: b.id.slice(0, 24), x: b.x, y: b.y, w: b.w, h: b.h, ...(b.radius ? { radius: b.radius } : {}) });
      continue;
    }
    boxes.push(structuredClone(b));
  }
  const raw = {
    id: opts.id,
    name: opts.name,
    base: s.layout,
    place: s.place ?? {},
    boxes,
    prompts,
    slots,
    ...(s.image && keep.has("image") ? { image: s.image } : {}),
    bg: s.bg ?? null,
    glyph: opts.glyph ?? [],
  };
  const l = cleanSavedLayout(raw);
  if (!l) throw new ThemeRefused("That slide could not be kept as a layout. Give it a name.");
  return l;
}

/** The id a layout's box has on a slide made from it: the layout's id and the box's own, so the slide knows where it came from. */
export const boxFromLayout = (l: Pick<SavedLayout, "id">, boxId: string) => `${l.id}-${boxId}`.slice(0, 40);

const sameJson = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
/** Whether a box on a slide is as a layout put it there: the same but for its layer, which the slide may have moved. */
const asLaid = (b: SlideBox, l: SavedLayout) => (layoutBase(l).boxes ?? []).some((lb) => b.id === boxFromLayout(l, lb.id) && sameJson({ ...b, z: 0 }, { ...startBox(l, lb), z: 0 }));

/** A layout's box as a slide made from it starts with it: a box with words starts empty, its words its prompt. */
function startBox(l: SavedLayout, b: SlideBox): SlideBox {
  const box = { ...structuredClone(b), id: boxFromLayout(l, b.id) };
  if ((b.kind === "text" || b.kind === "shape") && boxWords(b.text)) delete box.text;
  return box;
}

/**
 * What a slide becomes with a layout of the user's own on it: the layout's fields where it puts them (the slide's
 * words stay in its fields, so they move into the layout's places), its boxes and background, and the empty places
 * for pictures it has. Boxes an earlier layout of the user's put on the slide and nobody changed since go; anything
 * the user changed or typed in stays. A patch of the slide's fields, written as one step.
 */
export function slideWithLayout(s: LayoutSlide, l0: SavedLayout, layouts: SavedLayout[]): Partial<LayoutSlide> & { lay: string } {
  // SC: a layout edited in the slide master gives a new slide the pieces it was first made with, which the slide face
  // brings up to what the layout is now (shared/slideMaster.ts), so this slide follows its later edits too
  const l = { ...l0, ...layoutBase(l0) };
  const prev = s.lay ? layouts.find((x) => x.id === s.lay) : null;
  const pristine = (b: SlideBox) => !!prev && asLaid(b, prev);
  const mine = (s.boxes ?? []).filter((b) => !pristine(b));
  const ids = new Set(mine.map((b) => b.id));
  const added = (l.boxes ?? []).map((b) => startBox(l, b)).filter((b) => !ids.has(b.id));
  // the layout's boxes go behind the slide's own, in their own order
  const low = Math.min(0, ...mine.map((b) => b.z));
  const boxes = [...added.map((b, i) => ({ ...b, z: b.z < 0 ? b.z : low - added.length + i })), ...mine];
  const prevBg = prev ? (layoutBase(prev).bg ?? null) : null;
  const ownBg = s.bg && !sameJson(s.bg, prevBg) ? s.bg : null;
  return {
    layout: l.base,
    place: structuredClone(l.place ?? {}),
    boxes,
    bg: ownBg ?? (l.bg ? structuredClone(l.bg) : null),
    ...(l.image && !s.image ? { image: l.image } : {}),
    lay: l.id,
  };
}

/**
 * A slide going back to one of the board's layouts from one of the user's: the boxes that layout put there untouched
 * go, with its places and its background unless the user changed them. A patch, as slideWithLayout's.
 */
export function slideWithoutLayout(s: LayoutSlide, layout: string, layouts: SavedLayout[]): Omit<Partial<LayoutSlide>, "lay"> & { lay: null } {
  const prev = s.lay ? layouts.find((x) => x.id === s.lay) : null;
  if (!prev) return { layout, lay: null };
  const boxes = (s.boxes ?? []).filter((b) => !asLaid(b, prev));
  const base = layoutBase(prev);
  const place = sameJson(s.place ?? {}, base.place ?? {}) ? {} : (s.place ?? {});
  const bg = s.bg && sameJson(s.bg, base.bg ?? null) ? null : (s.bg ?? null);
  return { layout, boxes, place, bg, lay: null };
}

/** The prompt a slide's empty field or box shows: its layout's, when the slide was made from a layout of the user's. */
export function promptFor(layouts: SavedLayout[] | undefined, lay: string | undefined, what: PromptField | { box: string }): string | null {
  const l = lay ? layouts?.find((x) => x.id === lay) : null;
  if (!l) return null;
  if (typeof what === "string") return l.prompts?.[what] ?? null;
  const b = (l.boxes ?? []).find((x) => boxFromLayout(l, x.id) === what.box);
  return b ? boxWords(b.text) || null : null;
}

// ---------------------------------------------------------------------------
// saved themes, the workspace's

export interface SavedDeckTheme {
  id: string;
  /** raised each time it is saved again, so a deck that wears an older one can offer "Update this deck" */
  v: number;
  theme: Omit<DeckTheme, "from">;
  /** the layouts that travel with it */
  layouts: SavedLayout[];
  createdAt: number;
  updatedAt: number;
  /** who saved it: the user, or an agent by name */
  by: string | null;
}

export const savedThemeId = (rand: () => string) => "dt_" + rand().replace(/[^A-Za-z0-9]/g, "").slice(0, 16);
export const isSavedThemeId = (x: unknown): x is string => typeof x === "string" && SAVED_ID.test(x);

/** A saved theme as the workspace keeps it, or refused with words saying why. */
export function cleanSavedTheme(raw: unknown, ctx: { id: string; v: number; now: number; createdAt?: number; by: string | null }): SavedDeckTheme {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) throw new ThemeRefused("A theme is its name, its colours, its fonts and the rest, as an object.");
  const r = raw as Record<string, unknown>;
  const size = JSON.stringify(raw).length;
  if (size > MAX_THEME_BYTES) throw new ThemeRefused(`That theme is ${Math.round(size / 1000)}KB, over the ${MAX_THEME_BYTES / 1000}KB a theme keeps. Take out a layout or two.`);
  const t = cleanDeckTheme(r.theme ?? r);
  if (!t) throw new ThemeRefused("A theme needs its background and text colours, as #RRGGBB.");
  const { from: _from, ...theme } = t;
  if (!cleanName((r.theme as Record<string, unknown> | undefined)?.name ?? r.name, MAX_THEME_NAME)) throw new ThemeRefused("A theme needs a name.");
  return { id: ctx.id, v: ctx.v, theme, layouts: cleanLayouts(r.layouts), createdAt: ctx.createdAt ?? ctx.now, updatedAt: ctx.now, by: ctx.by };
}

/** A saved theme named as a person or an agent writes it: by its id or its name, any case. */
export function findSavedTheme(themes: SavedDeckTheme[], ref: unknown): SavedDeckTheme | null {
  if (typeof ref !== "string" || !ref.trim()) return null;
  const k = ref.trim().toLowerCase();
  return themes.find((t) => t.id.toLowerCase() === k) ?? themes.find((t) => t.theme.name.toLowerCase() === k) ?? null;
}

/** Whether a deck wears an older copy of a saved theme than the workspace keeps now. */
export function themeMovedOn(deckTheme: DeckTheme | null | undefined, saved: SavedDeckTheme[]): SavedDeckTheme | null {
  const from = deckTheme?.from;
  if (!from) return null;
  const s = saved.find((t) => t.id === from.id);
  return s && s.v > from.v ? s : null;
}

/** A saved theme as a deck wears it: its own copy of the values, saying which theme and version it came from. */
export function themeFromSaved(s: SavedDeckTheme): DeckTheme {
  return { ...structuredClone(s.theme), from: { id: s.id, v: s.v } };
}

// ---------------------------------------------------------------------------
// reading on the slide

function rgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16)) as [number, number, number];
}

function lum(c: [number, number, number]): number {
  const f = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]);
}

/** How two colours read on each other, as WCAG counts it (1 to 21). */
export function contrastOf(a: string, b: string): number {
  const x = rgb(a);
  const y = rgb(b);
  if (!x || !y) return 1;
  const la = lum(x);
  const lb = lum(y);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Two hex colours mixed, `t` of the way from the first to the second. */
export function mixHex(a: string, b: string, t: number): string {
  const x = rgb(a);
  const y = rgb(b);
  if (!x || !y) return a.toUpperCase();
  return "#" + x.map((v, i) => Math.round(v + (y[i] - v) * t).toString(16).padStart(2, "0")).join("").toUpperCase();
}

/** Whether a colour is dark: light words read on it. */
export const isDarkHex = (c: string) => {
  const x = rgb(c);
  return x ? lum(x) < 0.2 : false;
};

/**
 * Where a theme's words may be hard to read, in the words the editor shows: text and muted text on the background,
 * the accent on it (it marks one thing a slide, and is large), and text on the surface. A warning, never a refusal.
 */
export function readingWarnings(c: Palette): string[] {
  const out: string[] = [];
  if (contrastOf(c.text, c.background) < 4.5) out.push("Text is hard to read on the background.");
  if (contrastOf(c.muted, c.background) < 3) out.push("Muted text is hard to read on the background.");
  if (contrastOf(c.accent, c.background) < 3) out.push("The accent is hard to see on the background.");
  if (contrastOf(c.text, c.surface) < 4.5) out.push("Text is hard to read on the surface colour.");
  return out;
}

// ---------------------------------------------------------------------------
// designed starting points: palettes, font pairings, gradients

export interface PaletteSpec {
  id: string;
  name: string;
  colors: Palette;
}

/**
 * Starting palettes for a theme of one's own: calm grounds with one clear accent each, two darks, and a few bold ones.
 * Each reads (text 7:1 or better on its background, muted text 4.5:1, the accent 3:1; tests/slides-theme.test.ts).
 */
export const PALETTES: PaletteSpec[] = [
  { id: "porcelain", name: "Porcelain", colors: { background: "#F7F5F0", surface: "#ECE8DF", text: "#1C1B19", muted: "#5E5A52", accent: "#3451D1", accent2: "#0F7B6C", accent3: "#B8692A", accent4: "#7D52B8" } },
  { id: "graphite", name: "Graphite", colors: { background: "#FFFFFF", surface: "#F1F2F4", text: "#111214", muted: "#565A61", accent: "#E5482A", accent2: "#1F6FEB", accent3: "#12936E", accent4: "#B07A00" } },
  { id: "sage", name: "Sage", colors: { background: "#EEF1EA", surface: "#E0E6DA", text: "#1D2920", muted: "#4F5C53", accent: "#2E6A4E", accent2: "#B0602A", accent3: "#3B6A8A", accent4: "#94761C" } },
  { id: "clay", name: "Clay", colors: { background: "#F6EEE6", surface: "#EBDDD0", text: "#2A1F1A", muted: "#66554B", accent: "#AF4E26", accent2: "#2F5D62", accent3: "#A87A22", accent4: "#76497A" } },
  { id: "ink", name: "Ink", colors: { background: "#0F1115", surface: "#1B1E25", text: "#F3F2EE", muted: "#A6A8AF", accent: "#7FA8FF", accent2: "#5CC8A8", accent3: "#F2A65A", accent4: "#D18FD6" } },
  { id: "midnight", name: "Midnight", colors: { background: "#0E1630", surface: "#18223F", text: "#F2F4FA", muted: "#A8B2CE", accent: "#F5C451", accent2: "#6FD3C1", accent3: "#F28C6B", accent4: "#9DB4FF" } },
  { id: "forest", name: "Forest", colors: { background: "#112A22", surface: "#18372D", text: "#F1EFE6", muted: "#B1BDB2", accent: "#E7C46A", accent2: "#8FD1B5", accent3: "#E8936E", accent4: "#A9C1E8" } },
  { id: "cobalt", name: "Cobalt", colors: { background: "#1E3BD6", surface: "#2A47E0", text: "#FFFFFF", muted: "#D2DAFF", accent: "#FFD54F", accent2: "#7CF0C8", accent3: "#FFAA8A", accent4: "#C9D2FF" } },
  { id: "lemon", name: "Lemon", colors: { background: "#FFE45C", surface: "#F7D73A", text: "#16150F", muted: "#4A4421", accent: "#C42A1A", accent2: "#16150F", accent3: "#2747B8", accent4: "#0F6B4F" } },
];

export interface PairingSpec {
  id: string;
  name: string;
  title: string;
  body: string;
}

/** Title and body fonts that sit well together, offered before the full list. */
export const PAIRINGS: PairingSpec[] = [
  { id: "modern", name: "Modern", title: "geist", body: "geist" },
  { id: "editorial", name: "Editorial", title: "instrument-serif", body: "inter" },
  { id: "warm", name: "Warm", title: "fraunces", body: "manrope" },
  { id: "classic", name: "Classic", title: "playfair-display", body: "source-serif-4" },
  { id: "technical", name: "Technical", title: "space-grotesk", body: "ibm-plex-sans" },
  { id: "friendly", name: "Friendly", title: "plus-jakarta-sans", body: "plus-jakarta-sans" },
  { id: "studio", name: "Studio", title: "syne", body: "outfit" },
  { id: "poster", name: "Poster", title: "bebas-neue", body: "dm-sans" },
];

export interface GradientSpec {
  id: string;
  name: string;
  from: string;
  to: string;
  angle: number;
}

/** Quiet two-colour grounds: a light few and a dark few, each a small step from one colour to the next. The first follows the theme. */
export const GRADIENTS: GradientSpec[] = [
  { id: "theme", name: "From the theme", from: "background", to: "surface", angle: 160 },
  { id: "mist", name: "Mist", from: "#F4F6F9", to: "#E3E9F1", angle: 180 },
  { id: "dawn", name: "Dawn", from: "#FBF3EC", to: "#EFE9F6", angle: 135 },
  { id: "linen", name: "Linen", from: "#F8F3EA", to: "#ECE2D2", angle: 120 },
  { id: "meadow", name: "Meadow", from: "#F1F5EE", to: "#DFE9DE", angle: 160 },
  { id: "dusk", name: "Dusk", from: "#1A2040", to: "#3B2C50", angle: 135 },
  { id: "deep", name: "Deep sea", from: "#0A1C2A", to: "#14394B", angle: 165 },
  { id: "ember", name: "Ember", from: "#22140F", to: "#4A2519", angle: 145 },
];

/** The built-in styles' own values (src/boards/slides/look.ts draws them; tests/slides-theme.test.ts keeps both the same), for a theme an agent changes in part. */
export const STYLE_VALUES: Record<"paper" | "night" | "bold" | "editorial", Omit<DeckTheme, "from">> = {
  paper: { name: "Paper", colors: { background: "#FAF8F4", surface: "#F0ECE4", text: "#1D1C1A", muted: "#55514A", accent: "#C2410C", accent2: "#1F6F78", accent3: "#B8862B", accent4: "#6B5B95" }, fonts: { title: "geist", body: "geist" } },
  night: { name: "Night", colors: { background: "#141519", surface: "#1F2127", text: "#F2F1EE", muted: "#B8B6B0", accent: "#E9B44C", accent2: "#5FB3A8", accent3: "#E07A5F", accent4: "#8FA7D9" }, fonts: { title: "geist", body: "geist" } },
  bold: { name: "Bold", colors: { background: "#FFFFFF", surface: "#F1F3F7", text: "#0F1115", muted: "#474B54", accent: "#2F54EB", accent2: "#FF5A36", accent3: "#12A37A", accent4: "#FFB627" }, fonts: { title: "geist", body: "geist" }, key: "accent" },
  editorial: { name: "Editorial", colors: { background: "#F4EFE6", surface: "#EAE3D5", text: "#25211B", muted: "#5B5448", accent: "#9A3B2E", accent2: "#3D5A4C", accent3: "#B98C2F", accent4: "#2E4A6B" }, fonts: { title: "instrument-serif", body: "geist" } },
};

/** The theme colours' names, for words an agent and the editor read. */
export const COLOR_NAMES: Record<ThemeColorId, string> = Object.fromEntries(THEME_COLORS.map((c) => [c.id, c.name])) as Record<ThemeColorId, string>;

/** A background in a few words: for read_slides, and the control's own label. */
export function bgWords(bg: SlideBg | null | undefined): string {
  if (!bg) return "the theme's";
  const c = (x: string) => (x.startsWith("#") ? x : COLOR_NAMES[x as ThemeColorId]?.toLowerCase() ?? x);
  if (bg.kind === "color") return `colour ${c(bg.color)}`;
  if (bg.kind === "gradient") return `gradient ${c(bg.from)} to ${c(bg.to)} at ${bg.angle}°`;
  const veil = bg.veil ? `, ${bg.veil < 0 ? "darkened" : "lightened"} ${Math.round(Math.abs(bg.veil) * 100)}%` : "";
  return `picture ${bg.image.key} (${bg.fit === "fit" ? "fits inside" : "fills"}${veil})`;
}
